import React, { useState, useEffect } from "react";
import { FaLaptopCode, FaPaintBrush, FaCogs, FaBriefcase } from "react-icons/fa";
import { motion } from "framer-motion";

// 🔭 Star + Meteor Background Component
const StarBackground = () => {
  const [stars, setStars] = useState([]);
  const [meteors, setMeteors] = useState([]);
  const [parallaxX, setParallaxX] = useState(0);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    generateStars();
    generateMeteors();

    const handleResize = () => generateStars();
    const handleMouseMove = (e) => {
      setParallaxX((e.clientX / window.innerWidth - 0.5) * 10);
      setParallaxY((e.clientY / window.innerHeight - 0.5) * 10);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const generateStars = () => {
    const numberOfStars = Math.floor((window.innerWidth * window.innerHeight) / 8000);
    const newStars = Array.from({ length: numberOfStars }, (_, i) => ({
      id: i,
      size: Math.random() * 2 + 1,
      x: Math.random() * 100,
      y: Math.random() * 100,
      opacity: Math.random() * 0.6 + 0.3,
      speed: Math.random() * 2 + 1,
    }));
    setStars(newStars);
  };

  const generateMeteors = () => {
    const numberOfMeteors = 4;
    const newMeteors = Array.from({ length: numberOfMeteors }, (_, i) => ({
      id: i,
      size: Math.random() * 1.5 + 1,
      x: Math.random() * 100,
      y: Math.random() * 20,
      delay: Math.random() * 10,
      animationDuration: Math.random() * 3 + 3,
    }));
    setMeteors(newMeteors);
  };

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {stars.map((star) => (
        <div
          key={star.id}
          style={{
            width: `${star.size}px`,
            height: `${star.size}px`,
            left: `${star.x}%`,
            top: `${star.y}%`,
            opacity: star.opacity,
            position: "absolute",
            borderRadius: "9999px",
            backgroundColor: "white",
            boxShadow: "0 0 15px 3px rgba(255, 255, 255, 0.5)",
            animation: `twinkle ${star.speed}s ease-in-out infinite`,
            transform: `translate(${parallaxX * (star.speed / 2)}px, ${parallaxY * (star.speed / 2)}px)`,
          }}
        />
      ))}

      {meteors.map((meteor) => (
        <div
          key={meteor.id}
          style={{
            width: `${meteor.size * 50}px`,
            height: `${meteor.size * 2}px`,
            left: `${meteor.x}%`,
            top: `${meteor.y}%`,
            animationDelay: `${meteor.delay}s`,
            animationDuration: `${meteor.animationDuration}s`,
            position: "absolute",
            borderRadius: "9999px",
            background: "linear-gradient(to right, white, transparent)",
            boxShadow: "0 0 20px 5px rgba(255, 255, 255, 0.3)",
            transform: "rotate(-45deg)",
            animationName: "meteor",
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
          }}
        />
      ))}

      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        @keyframes meteor {
          0% { transform: translate(0, 0) rotate(-45deg); opacity: 1; }
          100% { transform: translate(300px, 300px) rotate(-45deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const servicesData = [
  {
    title: "Full-Stack Web Development",
    description:
      "Building responsive, performant web apps using React, Node.js, Express, and MongoDB.",
    icon: <FaLaptopCode className="text-emerald-400 text-4xl mb-4" />,
    borderColor: "border-emerald-500/30",
    hoverShadow: "hover:shadow-emerald-400/30",
    category: "development",
  },
  {
    title: "UI/UX Design",
    description:
      "Crafting clean, user-friendly interfaces focused on usability and visual appeal.",
    icon: <FaPaintBrush className="text-sky-400 text-4xl mb-4" />,
    borderColor: "border-sky-500/30",
    hoverShadow: "hover:shadow-sky-400/30",
    category: "design",
  },
  {
    title: "API Integration",
    description:
      "Integrating REST APIs for seamless backend communication and data flow.",
    icon: <FaCogs className="text-indigo-400 text-4xl mb-4" />,
    borderColor: "border-indigo-500/30",
    hoverShadow: "hover:shadow-indigo-400/30",
    category: "development",
  },
  {
    title: "Freelance Development",
    description:
      "Delivering end-to-end freelance projects tailored to client needs with clear timelines and high-quality results.",
    icon: <FaBriefcase className="text-pink-400 text-4xl mb-4" />,
    borderColor: "border-pink-500/30",
    hoverShadow: "hover:shadow-pink-400/30",
    category: "freelance",
  },
];

const Services = () => {
  const [filter, setFilter] = useState("all");
  const filteredServices =
    filter === "all" ? servicesData : servicesData.filter((s) => s.category === filter);

  return (
    <section className="relative text-white px-4 sm:px-6 py-16 sm:py-24 min-h-screen overflow-hidden">
      <StarBackground />
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-gray-800 opacity-80 z-0" />

      <div className="relative z-10 max-w-6xl mx-auto text-center">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl font-extrabold mb-4 bg-gradient-to-r from-emerald-400 via-sky-400 to-indigo-500 text-transparent bg-clip-text"
        >
          My Services
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-lg text-slate-300 mb-12 max-w-2xl mx-auto"
        >
          I help businesses and individuals build reliable digital solutions from idea to execution.
        </motion.p>

        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          {["all", "development", "design", "freelance"].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-4 py-2 rounded-full border text-sm transition-all duration-300 ${
                filter === type
                  ? "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white"
                  : "border-slate-500 text-slate-300 hover:bg-slate-700"
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {filteredServices.map((service, index) => (
            <motion.div
              key={index}
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className={`bg-white/5 backdrop-blur-md rounded-xl p-6 border ${service.borderColor} shadow-md ${service.hoverShadow} transition-shadow`}
            >
              <div className="flex flex-col items-center text-center">
                {service.icon}
                <h3 className="text-xl font-semibold text-white mb-2">{service.title}</h3>
                <p className="text-slate-300 text-sm">{service.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;